import { UserRole } from '@data/enums/user-role.enum'

/**
 * Ruta a la que aterriza cada rol tras login exitoso (o al cargar `/`
 * estando ya autenticado).
 *
 * Source of truth única — `LoginComponent` y `NoAuthGuard` la consumen.
 * El `Record<UserRole, string>` fuerza en compile-time que estén las
 * entradas para TODOS los roles del enum: si se agrega un rol nuevo a
 * `UserRole`, el build falla hasta que se mapee aquí. Esa es la red
 * de seguridad.
 *
 * Política definida 2026-05-22 (post ajuste backend de permisos
 * Dashboard). Documentada en:
 * - `rendafin-front-v2/CLAUDE.md` (sección "Roles que operan en este panel").
 * - `rendafin-front-v2/.claude/auth.md` (sección "Redirect post-login por rol").
 * - `Software_Docs/_vault/02-Architecture/Roles-Capabilities-Matrix.md`.
 *
 * NOTA sobre los ROLES EXTERNOS (`CLIENT`, `BROKER`, `MASTER_BROKER`): no operan
 * en este panel — su casa es `digital-channel` (y `mobile-rendafin` para el
 * Cliente). Se les mapea a `/login` a propósito, y ese valor es lo que
 * `operatesInThisPanel()` lee para negarles la entrada.
 *
 * `CONTABILIDAD` es interno y aterriza en `/cobranza/central`, no en el Dashboard: CENTRAL es su
 * pantalla de trabajo. **No tiene el módulo `leases`**, así que el expediente del arrendamiento le
 * queda cerrado a propósito — las 19 pestañas de `/lease/:id` no están gateadas una por una, y darle
 * entrada sería darle Comité, Negociación, Algoritmo y Análisis de riesgo. Su detalle por
 * arrendamiento vive dentro de Cobranza, sobre el libro mayor.
 *
 * `SELLER` NO es externo pese a que su correo de bienvenida apunte al canal
 * digital: es personal interno y sí opera aquí. El backend hace la misma
 * distinción — `usesClientChannel()` incluye a Seller (es sobre a qué app
 * apunta el email), `isExternalDocumentUploader()` no (es sobre quién es
 * externo). Ver `api-rendafin/src/core/auth/constants/role-names.ts:56,68`.
 */
export const ROLE_LANDING_ROUTE: Record<UserRole, string> = {
  [UserRole.ADMIN]: '/',
  [UserRole.OPERACIONES]: '/',
  [UserRole.FINANZAS]: '/',
  //* Contabilidad aterriza en Cobranza y no en el Dashboard: su trabajo ES esa pantalla.
  //* No entra a `/lease` — ver la nota de abajo.
  [UserRole.CONTABILIDAD]: '/cobranza/central',
  [UserRole.SELLER]: '/quote',
  [UserRole.BROKER]: '/login',
  [UserRole.MASTER_BROKER]: '/login',
  [UserRole.LEGAL]: '/lease',
  [UserRole.CLIENT]: '/login',
}

/**
 * Resuelve la landing route de un rol. Si el string no matchea ningún
 * `UserRole` conocido (rol nuevo en backend que aún no se reflejó aquí),
 * cae en `/login` como fallback defensivo.
 */
export function resolveLandingRoute(roleName: string | undefined | null): string {
  if (!roleName) return '/login'
  const route = ROLE_LANDING_ROUTE[roleName as UserRole]
  return route ?? '/login'
}

/**
 * ¿Este rol opera en este panel?
 *
 * Se DERIVA del mapa de arriba en vez de mantener una segunda lista: un rol sin
 * destino privado no tiene nada que hacer aquí, y tener dos fuentes para el mismo
 * hecho es pedir que se desincronicen. Agregar un rol externo se reduce entonces a
 * mapearlo a `/login`.
 *
 * Es **fail-closed**: un rol que el front no conozca cae a `/login` y queda fuera.
 * Preferimos negar de más —se nota al instante y se arregla mapeándolo— que dejar
 * entrar a alguien porque nadie se acordó de listarlo.
 *
 * Lo consume `AuthGuard` ANTES de evaluar permisos, porque `hasModulePermission`
 * decide por permiso y no por identidad. Ver `docs/security.md` § regla 4.
 */
export function operatesInThisPanel(roleName: string | undefined | null): boolean {
  return resolveLandingRoute(roleName) !== '/login'
}
